/**
 * Central de Gestão de Usuários M365.
 *
 * As rotas usam `ConsoleLayout` DIRETO, e não o `FullLayout`: o módulo é
 * exclusivo do design system novo (shadcn + Tailwind). Assim a tela renderiza
 * no shell Corporate Console independentemente da preferência `hub_shell` do
 * usuário, e não existe uma versão Vuetify a manter em paralelo.
 *
 * As 6 seções são rotas REAIS desde a primeira fase — é o que permite conceder
 * acesso por seção em /permissions e o que o syncFrontendRoutes cadastra. As
 * que ainda não têm tela entram como "Em breve".
 */
import { defineAsyncComponent } from 'vue';
import type { RouteRecordRaw } from 'vue-router';

// Assíncrono pelo mesmo motivo do FullLayout: um import estático puxaria o CSS
// do design system (Tailwind incluso) para o bundle de entrada.
const ConsoleLayout = defineAsyncComponent(() => import('@/layouts/console/ConsoleLayout.vue'));

const base = { requiresAuth: true, masterOnly: true, module: 'm365' } as const;

export const m365Routes: RouteRecordRaw[] = [
  {
    path: '/m365',
    component: ConsoleLayout,
    meta: { ...base, title: 'Microsoft 365' },
    children: [
      { path: '', redirect: '/m365/usuarios' },
      {
        path: 'usuarios',
        name: 'M365Usuarios',
        component: () => import('@/views/M365/UsersDirectory.vue'),
        meta: { ...base, title: 'Usuários', breadcrumb: 'Microsoft 365 / Usuários' },
      },
      {
        path: 'grupos',
        name: 'M365Grupos',
        component: () => import('@/views/M365/Groups.vue'),
        meta: { ...base, title: 'Grupos', breadcrumb: 'Microsoft 365 / Grupos' },
      },
      {
        path: 'seguranca',
        name: 'M365Seguranca',
        component: () => import('@/views/M365/Security.vue'),
        meta: { ...base, title: 'Segurança', breadcrumb: 'Microsoft 365 / Segurança' },
      },
      {
        path: 'dispositivos',
        name: 'M365Dispositivos',
        component: () => import('@/views/M365/Devices.vue'),
        meta: { ...base, title: 'Dispositivos', breadcrumb: 'Microsoft 365 / Dispositivos' },
      },
      {
        path: 'caixas-compartilhadas',
        name: 'M365Caixas',
        component: () => import('@/views/M365/SharedMailboxes.vue'),
        meta: {
          ...base,
          title: 'Caixas compartilhadas',
          breadcrumb: 'Microsoft 365 / Caixas compartilhadas',
        },
      },
      {
        // Fase I: a Fase C recusava estas listas (o Graph não gerencia membro de
        // lista de distribuição); o cliente do Exchange da Fase F destravou.
        path: 'listas',
        name: 'M365Listas',
        component: () => import('@/views/M365/DistributionLists.vue'),
        meta: {
          ...base,
          title: 'Listas de transmissão',
          breadcrumb: 'Microsoft 365 / Listas de transmissão',
        },
      },
      {
        // Rota própria (Fase G): auditar não pode exigir ser admin do console —
        // o admin é justamente quem a trilha existe para vigiar.
        path: 'auditoria',
        name: 'M365Auditoria',
        component: () => import('@/views/M365/AuditLog.vue'),
        meta: { ...base, title: 'Auditoria', breadcrumb: 'Microsoft 365 / Auditoria' },
      },
      {
        path: 'configuracoes',
        name: 'M365Configuracoes',
        component: () => import('@/views/M365/M365Settings.vue'),
        meta: { ...base, title: 'Configurações', breadcrumb: 'Microsoft 365 / Configurações' },
      },
    ],
  },
];

export default m365Routes;
